import { type ReactElement, type ReactNode, useEffect, useState } from 'react'

/**
 * `<img>` that degrades to `fallback` instead of the browser's broken-image
 * tile. Catalog images resolve through presigned S3 reads (`/api/s3/object`),
 * so a missing object, bucket misconfig (e.g. `MENUS_ASSET_BUCKET` unset), or
 * revoked IAM read surfaces as an <img> load error — every ordering surface
 * must show its normal placeholder in that case, never a broken tile.
 *
 * Also renders `fallback` for a null/empty `src`, so call sites don't need
 * their own ternary.
 */
export function AssetImage({
  src,
  alt = '',
  className,
  fallback,
  loading = 'lazy',
}: {
  src: string | null | undefined
  alt?: string
  className?: string
  fallback: ReactNode
  /**
   * Defaults to lazy: every current call site is a grid/list row, and each
   * catalog image costs a presign round-trip on the server — off-screen rows
   * must not compete with the page's data fetches. Pass 'eager' for a
   * guaranteed above-the-fold hero.
   */
  loading?: 'lazy' | 'eager'
}): ReactElement {
  const [failed, setFailed] = useState(false)
  // A new asset gets a fresh chance — reset the error latch when src changes.
  useEffect(() => {
    setFailed(false)
  }, [src])
  if (!src || failed) {
    return <>{fallback}</>
  }
  return (
    <img src={src} alt={alt} className={className} loading={loading} decoding="async" onError={() => setFailed(true)} />
  )
}
